import { assertNever } from "@sa-nu/shared-utils/misc";

import type { FeeModifierType } from "../../../../__generated__/gql/graphql";

type BaseFeePolicy = { unit: number; unitFeeAmount: number };

export type FacilityDiscount = {
  label: string;
  discountModifierType: FeeModifierType;
  discountValue: number;
};

export type FacilityFee = {
  totalFeeAmount: number;
  discount: { label: string; amount: number } | null;
  chargedFeeAmount: number;
};

export function calculateFacilityFee(
  baseFeePolicies: ReadonlyArray<BaseFeePolicy>,
  units: number | null,
  discount: FacilityDiscount | null,
): FacilityFee | null {
  const totalFeeAmount = calculateTotalFeeAmount(baseFeePolicies, units);
  if (totalFeeAmount === null) return null;

  const amount = discount ? calculateFeeModifier(totalFeeAmount, discount) : 0;
  if (!discount || amount <= 0) {
    return { totalFeeAmount, discount: null, chargedFeeAmount: totalFeeAmount };
  }
  return {
    totalFeeAmount,
    discount: { label: discount.label, amount },
    chargedFeeAmount: totalFeeAmount - amount,
  };
}

function calculateTotalFeeAmount(
  baseFeePolicies: ReadonlyArray<BaseFeePolicy>,
  units: number | null,
): number | null {
  if (units === null || units <= 0) return null;
  const policy =
    baseFeePolicies.find((p) => p.unit === units) ??
    baseFeePolicies.find((p) => p.unit === 1);
  return policy ? Math.round(policy.unitFeeAmount * units) : null;
}

function calculateFeeModifier(
  baseAmount: number,
  { discountModifierType, discountValue }: FacilityDiscount,
): number {
  switch (discountModifierType) {
    case "Value":
      return Math.min(discountValue, baseAmount);
    case "Percentage":
      return Math.min(
        Math.round((baseAmount * discountValue) / 100),
        baseAmount,
      );
    default:
      assertNever(discountModifierType);
  }
}

export type BaseFeeTier = { units: number; totalFeeAmount: number };

export type BaseFee =
  | { type: "free" }
  | { type: "uniform"; tiers: BaseFeeTier[] }
  | { type: "range"; min: number; max: number };

export function buildBaseFee(
  resources: ReadonlyArray<{
    baseFeePolicies: ReadonlyArray<{ unit: number; unitFeeAmount: number }>;
  }>,
): BaseFee | null {
  const scoped = resources
    .map((r) => r.baseFeePolicies)
    .filter((policies) => policies.some((p) => p.unit === 1));

  if (scoped.length === 0) return null;

  const rates = scoped.map(
    (policies) => policies.find((p) => p.unit === 1)!.unitFeeAmount,
  );
  const min = Math.min(...rates);
  const max = Math.max(...rates);

  if (min !== max) return { type: "range", min, max };
  if (max === 0) return { type: "free" };

  const tiers = [...scoped[0]!]
    .sort((a, b) => a.unit - b.unit)
    .map((p) => ({
      units: p.unit,
      totalFeeAmount: Math.round(p.unitFeeAmount * p.unit),
    }));

  return { type: "uniform", tiers };
}
